'use client';
import React, { useEffect, useState } from 'react';

interface Trend {
  niche: string;
  keyword: string;
  velocity_score: number;
  source: string;
}

interface TrendsPanelProps {
  onSelectTrend?: (keyword: string) => void;
}

export default function TrendsPanel({ onSelectTrend }: TrendsPanelProps) {
  const [trends, setTrends] = useState<Trend[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/trends');
      const data = await res.json();
      setTrends(data.trends ?? []);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrends();
    const interval = setInterval(fetchTrends, 30000);
    return () => clearInterval(interval);
  }, []);

  const getVelocityColor = (score: number) => {
    if (score >= 80) return '#C2185B';
    if (score >= 60) return '#ff7043';
    return '#ffa726';
  };

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(194,24,91,0.15)' }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: '#C2185B' }}
          />
          <span className="text-sm font-semibold" style={{ color: 'var(--chrome)' }}>
            Live Trend Signals
          </span>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs" style={{ color: 'var(--chrome-dim)' }}>
              {lastUpdated}
            </span>
          )}
          <button
            onClick={fetchTrends}
            className="text-xs px-3 py-1 rounded-lg transition-all"
            style={{
              background: 'rgba(194,24,91,0.1)',
              color: 'var(--ruby)',
              border: '1px solid rgba(194,24,91,0.2)',
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-12 rounded-xl animate-pulse"
              style={{ background: 'rgba(255,255,255,0.04)' }}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {trends.slice(0, 6).map((trend, i) => (
            <button
              key={i}
              onClick={() => onSelectTrend?.(trend.keyword)}
              className="w-full flex items-center justify-between p-3 rounded-xl transition-all text-left group"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(194,24,91,0.08)';
                e.currentTarget.style.borderColor = 'rgba(194,24,91,0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
              }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-md flex-shrink-0"
                  style={{
                    background: 'rgba(194,24,91,0.15)',
                    color: 'var(--ruby)',
                  }}
                >
                  #{i + 1}
                </span>
                <div className="min-w-0">
                  <p
                    className="text-sm font-medium truncate"
                    style={{ color: 'var(--chrome)' }}
                  >
                    {trend.keyword}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--chrome-dim)' }}>
                    {trend.niche} · {trend.source}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <div className="flex items-center gap-1">
                  <div
                    className="w-16 h-1.5 rounded-full overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.08)' }}
                  >
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${trend.velocity_score}%`,
                        background: getVelocityColor(trend.velocity_score),
                      }}
                    />
                  </div>
                  <span
                    className="text-xs font-mono font-bold"
                    style={{ color: getVelocityColor(trend.velocity_score) }}
                  >
                    {trend.velocity_score}
                  </span>
                </div>
                <span
                  className="text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: 'var(--ruby)' }}
                >
                  Use →
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
